import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Users, Check, X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const CATEGORY_ORDER = ['Core', 'Academic', 'Finance', 'AI', 'Analytics', 'Communication', 'Operations', 'System'];

const PLAN_COLORS = {
  gray: { bg: 'bg-slate-50', badge: 'bg-slate-100 text-slate-700', border: 'border-slate-200', dot: 'bg-slate-400' },
  blue: { bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  purple: { bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  green: { bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
};

const fmt = (n) => n === 0 ? 'Free' : `₦${n.toLocaleString()}`;

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const emptyForm = {
    name: '', description: '', student_limit: 200,
    term_price: 0, session_price: 0,
    term_days: 90, session_days: 270,
    color: 'blue', features: [], modules: [], is_active: true,
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, modulesRes] = await Promise.all([
        api.get('/master/plans'),
        api.get('/master/modules'),
      ]);
      setPlans(plansRes.data || []);
      setModules(modulesRes.data || []);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm({ ...emptyForm, modules: modules.map(m => m.key) }); setEditingPlan(null); setShowForm(true); };
  const openEdit = (plan) => {
    setForm({ ...plan, features: plan.features || [], modules: plan.modules || [] });
    setEditingPlan(plan);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleModule = (key) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.includes(key) ? prev.modules.filter(m => m !== key) : [...prev.modules, key],
    }));
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Plan name is required');
    setSaving(true);
    try {
      if (editingPlan) {
        await api.put(`/master/plans/${editingPlan.id}`, form);
        toast.success('Plan updated');
      } else {
        await api.post('/master/plans', form);
        toast.success('Plan created');
      }
      setShowForm(false);
      setEditingPlan(null);
      fetchData();
    } catch (err) {
      toast.error(err?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}" plan? This cannot be undone.`)) return;
    setDeleting(plan.id);
    try {
      await api.delete(`/master/plans/${plan.id}`);
      toast.success('Plan deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.detail || 'Cannot delete this plan');
    } finally {
      setDeleting(null);
    }
  };

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = modules.filter(m => m.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Plans & Pricing</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage subscription plans and module entitlements</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          data-testid="create-plan-btn"
        >
          <Plus className="w-4 h-4" /> New Plan
        </button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5" data-testid="plan-form">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Plan Name *</label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Standard" data-testid="plan-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Color Theme</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))}>
                <option value="gray">Gray (Free)</option>
                <option value="blue">Blue</option>
                <option value="purple">Purple (Premium)</option>
                <option value="green">Green</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Description</label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Short plan description" />
            </div>

            {/* Termly Pricing */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Termly Price (₦)</label>
              <input type="number" min="0" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.term_price} onChange={e => setForm(p => ({ ...p, term_price: Number(e.target.value) }))} data-testid="plan-term-price-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Term Duration (days)</label>
              <input type="number" min="1" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.term_days} onChange={e => setForm(p => ({ ...p, term_days: Number(e.target.value) }))} placeholder="e.g. 90" data-testid="plan-term-days-input" />
              <p className="text-[10px] text-slate-400 mt-1">e.g. 90 days ≈ 1 school term (~3 months)</p>
            </div>

            {/* Full Session Pricing */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Full Session Price (₦)</label>
              <input type="number" min="0" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.session_price} onChange={e => setForm(p => ({ ...p, session_price: Number(e.target.value) }))} data-testid="plan-session-price-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Session Duration (days)</label>
              <input type="number" min="1" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.session_days} onChange={e => setForm(p => ({ ...p, session_days: Number(e.target.value) }))} placeholder="e.g. 270" data-testid="plan-session-days-input" />
              <p className="text-[10px] text-slate-400 mt-1">e.g. 270 days ≈ 1 full session (3 terms)</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Student Limit</label>
              <input type="number" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                value={form.student_limit} onChange={e => setForm(p => ({ ...p, student_limit: Number(e.target.value) }))} />
            </div>
            <div className="flex items-center gap-3 pt-5">
              <input type="checkbox" id="plan-active" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="w-4 h-4 accent-indigo-600" />
              <label htmlFor="plan-active" className="text-sm text-slate-700">Active (visible to schools)</label>
            </div>
          </div>

          {/* Module Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs font-medium text-slate-700">Included Modules</label>
              <button
                type="button"
                onClick={() => setForm(p => ({
                  ...p,
                  modules: p.modules.length === modules.length ? [] : modules.map(m => m.key),
                }))}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="select-all-modules-btn"
              >
                {form.modules.length === modules.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-3">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-2">{cat}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {items.map(m => (
                      <label key={m.key} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors text-sm
                        ${form.modules.includes(m.key) ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                        <input type="checkbox" className="hidden" checked={form.modules.includes(m.key)} onChange={() => toggleModule(m.key)} />
                        <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${form.modules.includes(m.key) ? 'bg-indigo-600' : 'bg-white border border-slate-300'}`}>
                          {form.modules.includes(m.key) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        {m.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">{form.modules.length} module(s) selected</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
              data-testid="save-plan-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </button>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map(plan => {
          const colors = PLAN_COLORS[plan.color] || PLAN_COLORS.blue;
          const isExpanded = expandedPlan === plan.id;
          return (
            <div key={plan.id} className={`bg-white rounded-xl border ${colors.border} shadow-sm overflow-hidden`} data-testid={`plan-card-${plan.name.toLowerCase()}`}>
              <div className={`px-5 py-4 ${colors.bg}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                    <h3 className="font-bold text-slate-900 text-base">{plan.name}</h3>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                    {plan.school_count || 0} school{plan.school_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{plan.description}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-900">
                    {fmt(plan.term_price || plan.monthly_price || 0)}
                  </span>
                  {(plan.term_price || plan.monthly_price || 0) > 0 && (
                    <span className="text-xs text-slate-400">/term ({plan.term_days || 90}d)</span>
                  )}
                </div>
                {(plan.session_price || plan.yearly_price || 0) > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {fmt(plan.session_price || plan.yearly_price || 0)}/session ({plan.session_days || 270}d)
                  </p>
                )}
              </div>

              <div className="px-5 py-4 space-y-3">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Users className="w-3.5 h-3.5 text-slate-400" />
                  Up to {plan.student_limit.toLocaleString()} students
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Check className="w-3.5 h-3.5 text-slate-400" />
                  {(plan.modules || []).length} modules included
                </div>

                {isExpanded && (
                  <div className="space-y-1 pt-1 border-t border-slate-100">
                    {(plan.modules || []).map(m => {
                      const mod = modules.find(x => x.key === m);
                      return (
                        <div key={m} className="flex items-center gap-2 text-xs text-slate-600">
                          <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          {mod?.label || m}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                >
                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {isExpanded ? 'Hide' : 'View'} modules
                </button>

                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button onClick={() => openEdit(plan)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    data-testid={`edit-plan-${plan.name.toLowerCase()}-btn`}>
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => handleDelete(plan)} disabled={deleting === plan.id}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    data-testid={`delete-plan-${plan.name.toLowerCase()}-btn`}>
                    {deleting === plan.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
